import React, { useEffect, useState } from 'react';
import './ResortDetail.css';

// 在组件外部定义辅助函数
const getMultiplierColor = (multiplier) => {
  if (multiplier >= 0.8) return '#4CAF50';  // 绿色
  if (multiplier >= 0.6) return '#FFC107';  // 黄色
  if (multiplier >= 0.4) return '#FF9800';  // 橙色
  return '#F44336';  // 红色
};

const ResortDetail = ({ resort, onClose }) => {
  console.log('ResortDetail received resort:', resort);
  
  const [tagData, setTagData] = useState({ tags: [], total: 0 });
  const [showAllTags, setShowAllTags] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  // 加载标签数据
  useEffect(() => {
    const fetchTags = async () => {
      if (!resort?.id) {
        console.log('No resort ID available');
        return;
      }
      
      try {
        console.log('Fetching tags for resort:', resort.id);
        const response = await fetch(
          `/api/tags?source=web&resortId=${resort.id}&limit=999&includeRanking=true&levelOrder=world,continent,country,region`
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch tags: ${response.status}`);
        }
        const data = await response.json();
        console.log('Received tag data:', data);
        setTagData({
          tags: Array.isArray(data?.tags) ? data.tags : [],
          total: data?.total || 0
        });
      } catch (error) {
        console.error('Failed to fetch tags:', error);
        setTagData({ tags: [], total: 0 });
      }
    };

    fetchTags();
  }, [resort?.id]);

  // 检查是否已收藏
  useEffect(() => {
    const checkFavorite = () => {
      const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
      setIsFavorite(favorites.some(fav => fav.id === resort.id));
    };
    
    if (resort?.id) {
      checkFavorite();
    }
  }, [resort?.id]);

  // 处理收藏/取消收藏
  const handleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    
    if (isFavorite) {
      // 取消收藏
      const newFavorites = favorites.filter(fav => fav.id !== resort.id);
      localStorage.setItem('favorites', JSON.stringify(newFavorites));
      setIsFavorite(false);
    } else {
      // 添加收藏
      const newFavorite = {
        id: resort.id,
        name: resort.name,
        timestamp: Date.now()
      };
      localStorage.setItem('favorites', JSON.stringify([...favorites, newFavorite]));
      setIsFavorite(true);
    }
  };

  if (!resort) {
    console.log('No resort data provided');
    return null;
  }

  // 添加拖动处理
  const handleMouseDown = (e) => {
    const table = e.currentTarget;
    const startX = e.pageX - table.offsetLeft;
    const scrollLeft = table.scrollLeft;

    const handleMouseMove = (e) => {
      const x = e.pageX - table.offsetLeft;
      const walk = (x - startX) * 2; // 乘以2使滚动更快
      table.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      table.style.cursor = 'grab';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    table.style.cursor = 'grabbing';
  };

  // 获取排序后的前三个最重要的标签
  const getImportantTags = (tags) => {
    const levelPriority = {
      'world': 4,
      'continent': 3,
      'country': 2,
      'region': 1
    };

    return [...tags].sort((a, b) => {
      // 首先按级别优先级排序
      const levelDiff = levelPriority[b.level] - levelPriority[a.level];
      if (levelDiff !== 0) return levelDiff;
      
      // 如果级别相同，则按分数排序
      return b.score - a.score;
    }).slice(0, 3);
  };

  return (
    <div className="resort-detail">
      {/* 雪道图 */}
      {resort.trailImage && (
        <div className="trail-map" onClick={() => setShowFullImage(true)}>
          <img src={resort.trailImage} alt={`${resort.name} 雪道图`} />
        </div>
      )}

      {/* 全屏雪道图 */}
      {showFullImage && (
        <div 
          className="fullscreen-image"
          onClick={() => setShowFullImage(false)}
        >
          <div className="fullscreen-content">
            <img src={resort.trailImage} alt={`${resort.name} 雪道图`} />
            <button 
              className="close-fullscreen"
              onClick={(e) => {
                e.stopPropagation();
                setShowFullImage(false);
              }}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* 基本信息 */}
      <div className="resort-info">
        <div className="resort-header">
          <h2>{resort.name}</h2>
          <div className="location">
            {resort.continent} · {resort.country} · {resort.region}
          </div>
          <div className="rankings">
            {Array.isArray(tagData.tags) && (
              <>
                {/* 显示前三个重要标签或全部标签 */}
                {(showAllTags ? tagData.tags : getImportantTags(tagData.tags)).map((tag, index) => (
                  <div key={index} className={`ranking-tag ${tag.level}`}>
                    {tag.name}
                  </div>
                ))}
                {/* 显示更多标签的按钮 */}
                {tagData.tags.length > 3 && (
                  <button 
                    className="more-tags-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowAllTags(!showAllTags);
                    }}
                  >
                    {showAllTags ? '收起' : `更多标签(${tagData.total})`}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* 雪场信息 */}
      <div className="info-section">
        <h3>雪场信息</h3>
        <div className="info-row">
          <div className="info-item">
            <label>雪场评分</label>
            <span>{resort.rating}</span>
          </div>
          <div className="info-item">
            <label>总长度</label>
            <span>{resort.totalTrails}km</span>
          </div>
          <div className="info-item">
            <label>海拔差</label>
            <span>{resort.verticalDrop}m</span>
          </div>
          <div className="info-item">
            <label>缆车数量</label>
            <span>{resort.lifts}台</span>
          </div>
        </div>
      </div>

      {/* 雪道信息 */}
      <div className="info-section">
        <h3>雪道信息</h3>
        <div className="info-row">
          <div className="info-item">
            <span className="trail-dot beginner"></span>
            <label>初级道</label>
            <span>{resort.beginnerTrails}km</span>
          </div>
          <div className="info-item">
            <span className="trail-dot intermediate"></span>
            <label>中级道</label>
            <span>{resort.intermediateTrails}km</span>
          </div>
          <div className="info-item">
            <span className="trail-dot advanced"></span>
            <label>高级道</label>
            <span>{resort.advancedTrails}km</span>
          </div>
        </div>
        <div className="trail-details">
          <div className="trail-detail-item">
            <div className="trail-detail-header">
              <span className="trail-detail-label">最长雪道</span>
              <span className="trail-detail-name">{resort.longestRunName || '-'}</span>
            </div>
            <div className="trail-detail-info">
              <div className="trail-detail-row">
                <span className="label">长度</span>
                <span>{resort.longestRunLength ? `${(resort.longestRunLength/1000).toFixed(1)}km` : '-'}</span>
              </div>
              <div className="trail-detail-row">
                <span className="label">落差</span>
                <span>{resort.longestRunDrop ? `${resort.longestRunDrop}m` : '-'}</span>
              </div>
            </div>
          </div>
          <div className="trail-detail-item">
            <div className="trail-detail-header">
              <span className="trail-detail-label">最难雪道</span>
              <span className="trail-detail-name">{resort.hardestRunName || '-'}</span>
            </div>
            <div className="trail-detail-info">
              <div className="trail-detail-row">
                <span className="label">长度</span>
                <span>{resort.hardestRunLength ? `${(resort.hardestRunLength/1000).toFixed(1)}km` : '-'}</span>
              </div>
              <div className="trail-detail-row">
                <span className="label">落差</span>
                <span>{resort.hardestRunDrop ? `${resort.hardestRunDrop}m` : '-'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 交通信息 */}
      <div className="info-section">
        <h3>交通信息</h3>
        <div className="transport-info">
          <div className="info-row">
            <div className="info-item">
              <label>最近城市</label>
              <div className="distance-info">
                <span className="location-name">{resort.nearestCity}</span>
                <span className="distance">{resort.nearestCityDistance}km</span>
              </div>
            </div>
            <div className="info-item">
              <label>最近大城市</label>
              <div className="distance-info">
                <span className="location-name">{resort.nearestPrefecture}</span>
                <span className="distance">{resort.nearestPrefectureDistance}km</span>
              </div>
            </div>
          </div>
          <div className="info-row">
            <div className="info-item">
              <label>最近机场</label>
              <div className="distance-info">
                <span className="location-name">{resort.nearestAirport}</span>
                <span className="distance">{resort.nearestAirportDistance}km</span>
              </div>
            </div>
            <div className="info-item">
              <label>最近火车站</label>
              <div className="distance-info">
                <span className="location-name">{resort.nearestStation}</span>
                <span className="distance">{resort.nearestStationDistance}km</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 气候信息 */}
      <div className="info-section">
        <h3>气候信息</h3>
        <div className="climate-info">
          <div className="info-row">
            <div className="info-item">
              <label>年均降雪</label>
              <span>{resort.snowFall}cm</span>
            </div>
            <div className="info-item">
              <label>雪均深</label>
              <span>{resort.snowDepth}cm</span>
            </div>
            <div className="info-item">
              <label>积雪期</label>
              <span>{resort.snowExistDays}天</span>
            </div>
          </div>
          <div className="info-row">
            <div className="info-item">
              <label>降雪日</label>
              <span>{resort.snowDaysYearly}日</span>
            </div>
            <div className="info-item">
              <label>温度评分</label>
              <span>{resort.temperature}分</span>
            </div>
            <div className="info-item">
              <label>风力评分</label>
              <span>{resort.windScore}分</span>
            </div>
          </div>
        </div>
      </div>

      {/* 未来天气 */}
      <div className="info-section">
        <h3>未来天气</h3>
        <div 
          className="weather-table"
          onMouseDown={handleMouseDown}
          onTouchStart={(e) => {
            // 防止触摸时页面滚动
            e.preventDefault();
          }}
        >
          <table>
            <thead>
              <tr>
                <th>指标</th>
                {resort.weatherForecast?.map(weather => (
                  <th key={weather.date}>{weather.date.slice(5)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>天气</td>
                {resort.weatherForecast?.map(weather => (
                  <td key={weather.date}>
                    <div className="weather-condition">
                      {weather.condition}
                      {weather.willSnow && <span className="snow-tag">降雪</span>}
                    </div>
                  </td>
                ))}
              </tr>
              <tr>
                <td>温度</td>
                {resort.weatherForecast?.map(weather => (
                  <td key={weather.date}>
                    <div className="temperature-range">
                      <span className="max-temp">{weather.maxTemp}°</span>
                      <span className="min-temp">{weather.minTemp}°</span>
                    </div>
                  </td>
                ))}
              </tr>
              <tr>
                <td>风速</td>
                {resort.weatherForecast?.map(weather => (
                  <td key={weather.date}>
                    <span className="wind-speed">
                      {weather.maxWindSpeed}km/h
                    </span>
                  </td>
                ))}
              </tr>
              <tr>
                <td>降雪量</td>
                {resort.weatherForecast?.map(weather => (
                  <td key={weather.date}>
                    <span className="snowfall">
                      {weather.snowfall >= 0 ? `${weather.snowfall}cm` : '-'}
                    </span>
                  </td>
                ))}
              </tr>
              <tr>
                <td>降雪概率</td>
                {resort.weatherForecast?.map(weather => (
                  <td key={weather.date}>
                    <span className="snow-chance">
                      {weather.chanceOfSnow >= 0 ? `${weather.chanceOfSnow}%` : '-'}
                    </span>
                  </td>
                ))}
              </tr>
              <tr>
                <td>适滑指数</td>
                {resort.weatherForecast?.map(weather => (
                  <td key={weather.date}>
                    <div className="weather-multiplier" 
                         style={{
                           background: getMultiplierColor(weather.weatherMultiplier)
                         }}>
                      {Math.round(weather.weatherMultiplier * 10)}
                    </div>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 底部按钮 */}
      <div className="detail-footer">
        <div className="button-row">
          <button className="nav-btn">导航前往</button>
          <button 
            className={`favorite-btn ${isFavorite ? 'active' : ''}`}
            onClick={handleFavorite}
          >
            {isFavorite ? '已收藏' : '收藏'}
          </button>
          <button className="book-btn">预订门票</button>
        </div>
        <button className="close-btn" onClick={onClose}>关闭</button>
      </div>
    </div>
  );
};

export default ResortDetail; 